import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, AlertCircle, User, CheckCircle, LogIn, UserCheck } from 'lucide-react';

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { user, login, loginWithGoogle, register, logout, oauthError, clearOauthError } = useAuth();

  // mode
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [registered, setRegistered] = useState(false);

  // login
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [mounted, setMounted]       = useState(false);

  // register
  const [regName, setRegName]       = useState('');
  const [regEmail, setRegEmail]     = useState('');
  const [regPass, setRegPass]       = useState('');
  const [regPass2, setRegPass2]     = useState('');
  const [showRegPass, setShowRegPass]   = useState(false);
  const [showRegPass2, setShowRegPass2] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError]     = useState<string | null>(null);

  // Muestra selector de cuenta en vez de redirigir automáticamente
  const [showAccountPicker, setShowAccountPicker] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (user) setShowAccountPicker(true);
    else setShowAccountPicker(false);
  }, [user]);

  function continueWithCurrentAccount() {
    if (!user) return;
    if (user.role === 'admin')             navigate('/dashboard');
    else if (user.role === 'distribuidor') navigate('/distribuidor/dashboard');
    else                                   navigate('/reseller/dashboard');
  }

  async function switchAccount() {
    setShowAccountPicker(false);
    await logout();
  }

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError(null);
    clearOauthError();
    try {
      await loginWithGoogle();
    } catch (err) {
      setError((err as Error)?.message ?? 'Error');
      setGoogleLoading(false);
    }
  };

  function switchMode(m: 'login' | 'register') {
    setMode(m);
    setError(null);
    setRegError(null);
    clearOauthError();
  }

  // Si no tiene @ lo convierte en email fake para Supabase
  function toEmail(value: string) {
    return value.includes('@') ? value : `${value.trim()}@xtreamsales.app`;
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError(null);
    if (!regName.trim()) { setRegError('El nombre es obligatorio'); return; }
    if (!regEmail.trim()) { setRegError('El usuario o correo es obligatorio'); return; }
    if (regPass.length < 6) { setRegError('La contraseña debe tener al menos 6 caracteres'); return; }
    if (regPass !== regPass2) { setRegError('Las contraseñas no coinciden'); return; }
    setRegLoading(true);
    try {
      await register(toEmail(regEmail), regPass, regName.trim());
      setRegistered(true);
    } catch (err) {
      setRegError((err as Error)?.message ?? 'Error');
    } finally {
      setRegLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { user: loggedUser } = await login(toEmail(email), password);
      if (loggedUser.role === 'admin')             navigate('/dashboard');
      else if (loggedUser.role === 'distribuidor') navigate('/distribuidor/dashboard');
      else                                         navigate('/reseller/dashboard');
    } catch (err) {
      setError((err as Error)?.message ?? 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lr">

      {/* ── Fondo ──────────────────────────────────────── */}
      <div className="lr-bg">
        <img src="/Fondo-login.webp" alt="" className="lr-bg-img" />
        <div className="lr-bg-vignette" />
        <div className="lr-bg-overlay" />
      </div>

      {/* ── Layout ─────────────────────────────────────── */}
      <main className={`lr-wrap ${mounted ? 'lr-wrap--in' : ''}`}>

        {/* 1 · Logo 360XS */}
        <div className="lr-brand">
          <img src="/360xs-logo.png" alt="360XS XtreamSales" className="lr-brand-img" />
        </div>

        {/* 2 · Formulario */}
        <div className="lr-card">
          <div className="lr-card-border" />

          {/* ── SELECTOR DE CUENTA ACTIVA ── */}
          {showAccountPicker && user ? (
            <div className="lr-picker">
              <div className="lr-picker-ico">
                <UserCheck size={28} />
              </div>
              <p className="lr-picker-title">Sesión activa</p>
              <p className="lr-picker-email">{user.email}</p>
              <span className="lr-picker-role">
                {user.role === 'admin' ? 'Administrador' : user.role === 'distribuidor' ? 'Distribuidor' : 'Reseller'}
              </span>
              <button type="button" className="lr-submit" onClick={continueWithCurrentAccount}>
                <span className="lr-submit-shine" />
                <LogIn size={15} style={{ position: 'relative', zIndex: 1, marginRight: 8 }} />
                <span className="lr-submit-text">Continuar como {user.email}</span>
              </button>
              <button type="button" className="lr-switch-btn" onClick={switchAccount}>
                Usar otra cuenta
              </button>
            </div>
          ) : (
          <>

          {/* Tab toggle */}
          <div className="lr-tabs">
            <button
              type="button"
              className={`lr-tab ${mode === 'login' ? 'lr-tab--active' : ''}`}
              onClick={() => switchMode('login')}
            >Iniciar sesión</button>
            <button
              type="button"
              className={`lr-tab ${mode === 'register' ? 'lr-tab--active' : ''}`}
              onClick={() => switchMode('register')}
            >Registrarse</button>
          </div>

          {/* ── LOGIN ── */}
          {mode === 'login' && (
            <form onSubmit={handleSubmit} className="lr-form">

              {/* Email / Usuario */}
              <div className="lr-field-group">
                <label className="lr-label">Usuario o correo electrónico</label>
                <div className="lr-field">
                  <Mail className="lr-field-ico" size={14} />
                  <input
                    type="text"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    placeholder="usuario o correo@ejemplo.com"
                    className="lr-input"
                    autoComplete="username"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="lr-field-group">
                <label className="lr-label">Contraseña</label>
                <div className="lr-field">
                  <Lock className="lr-field-ico" size={14} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="lr-input lr-input--pass"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="lr-eye"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              {(error || oauthError) && (
                <div className="lr-error">
                  <AlertCircle size={13} className="lr-error-ico" />
                  <span>{error || oauthError}</span>
                </div>
              )}

              <div className="lr-forgot">
                <button type="button" className="lr-forgot-btn">
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={googleLoading || loading}
                className="lr-google"
              >
                {googleLoading
                  ? <span className="lr-spinner" />
                  : (<><GoogleIcon /><span>Continuar con Google</span></>)
                }
              </button>

              <div className="lr-divider">
                <span className="lr-div-hr" />
                <span className="lr-div-label">o</span>
                <span className="lr-div-hr" />
              </div>

              <button type="submit" disabled={loading} className="lr-submit">
                <span className="lr-submit-shine" />
                {loading ? <span className="lr-spinner" /> : <span className="lr-submit-text">Ingresar</span>}
              </button>

            </form>
          )}

          {/* ── REGISTER ── */}
          {mode === 'register' && (
            registered ? (
              <div className="lr-success">
                <CheckCircle size={36} className="lr-success-ico" />
                <p className="lr-success-title">¡Registro exitoso!</p>
                <p className="lr-success-msg">Tu cuenta ha sido creada y está pendiente de activación. El administrador te habilitará el acceso en breve.</p>
                <button
                  type="button"
                  className="lr-submit"
                  style={{ marginTop: '6px' }}
                  onClick={() => { setRegistered(false); switchMode('login'); }}
                >
                  <span className="lr-submit-shine" />
                  <span className="lr-submit-text">Volver al inicio de sesión</span>
                </button>
              </div>
            ) : (
              <form onSubmit={handleRegister} className="lr-form">

                {/* Nombre */}
                <div className="lr-field-group">
                  <label className="lr-label">Nombre completo</label>
                  <div className="lr-field">
                    <User className="lr-field-ico" size={14} />
                    <input
                      type="text"
                      value={regName}
                      onChange={e => setRegName(e.target.value)}
                      required
                      placeholder="Tu nombre"
                      className="lr-input"
                      autoComplete="name"
                    />
                  </div>
                </div>

                {/* Usuario o correo */}
                <div className="lr-field-group">
                  <label className="lr-label">Usuario o correo electrónico</label>
                  <div className="lr-field">
                    <Mail className="lr-field-ico" size={14} />
                    <input
                      type="text"
                      value={regEmail}
                      onChange={e => setRegEmail(e.target.value)}
                      required
                      placeholder="usuario o correo@ejemplo.com"
                      className="lr-input"
                      autoComplete="username"
                    />
                  </div>
                </div>

                {/* Contraseña */}
                <div className="lr-field-group">
                  <label className="lr-label">Contraseña</label>
                  <div className="lr-field">
                    <Lock className="lr-field-ico" size={14} />
                    <input
                      type={showRegPass ? 'text' : 'password'}
                      value={regPass}
                      onChange={e => setRegPass(e.target.value)}
                      required
                      placeholder="Mínimo 6 caracteres"
                      className="lr-input lr-input--pass"
                      autoComplete="new-password"
                    />
                    <button type="button" onClick={() => setShowRegPass(!showRegPass)} className="lr-eye" tabIndex={-1}>
                      {showRegPass ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>

                {/* Confirmar contraseña */}
                <div className="lr-field-group">
                  <label className="lr-label">Confirmar contraseña</label>
                  <div className="lr-field">
                    <Lock className="lr-field-ico" size={14} />
                    <input
                      type={showRegPass2 ? 'text' : 'password'}
                      value={regPass2}
                      onChange={e => setRegPass2(e.target.value)}
                      required
                      placeholder="Repite la contraseña"
                      className="lr-input lr-input--pass"
                      autoComplete="new-password"
                    />
                    <button type="button" onClick={() => setShowRegPass2(!showRegPass2)} className="lr-eye" tabIndex={-1}>
                      {showRegPass2 ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>

                {regError && (
                  <div className="lr-error">
                    <AlertCircle size={13} className="lr-error-ico" />
                    <span>{regError}</span>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={googleLoading || regLoading}
                  className="lr-google"
                >
                  {googleLoading
                    ? <span className="lr-spinner" />
                    : (<><GoogleIcon /><span>Registrarse con Google</span></>)
                  }
                </button>

                <div className="lr-divider">
                  <span className="lr-div-hr" />
                  <span className="lr-div-label">o</span>
                  <span className="lr-div-hr" />
                </div>

                <button type="submit" disabled={regLoading} className="lr-submit">
                  <span className="lr-submit-shine" />
                  {regLoading ? <span className="lr-spinner" /> : <span className="lr-submit-text">Crear cuenta</span>}
                </button>

              </form>
            )
          )}

          </>
          )}
        </div>

        {/* 3 · Logos de plataforma */}
        <div className="lr-platforms">
          <div className="lr-plat lr-plat--red">
            <img
              src="/2onamedia-logo%20cuadrado.webp"
              alt="2ONA MEDIA"
              className="lr-plat-img"
            />
            <div className="lr-plat-shine" />
          </div>

          <div className="lr-plat-divider">
            <span className="lr-div-line" />
            <span className="lr-div-gem" />
            <span className="lr-div-line" />
          </div>

          <div className="lr-plat lr-plat--blue">
            <img
              src="/1tvlogin.webp"
              alt="Xuper Play"
              className="lr-plat-img"
            />
            <div className="lr-plat-shine" />
          </div>
        </div>

        {/* 4 · Tagline */}
        <p className="lr-tagline">
          <span className="lr-tag-dot lr-tag-dot--red" />
          2 Plataformas&nbsp;&nbsp;·&nbsp;&nbsp;Un solo sistema
          <span className="lr-tag-dot lr-tag-dot--blue" />
        </p>

      </main>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@300;400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        /* ── Root ─────────────────────────────────── */
        .lr {
          min-height: 100vh;
          min-height: 100dvh;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Exo 2', sans-serif;
          position: relative;
          overflow: hidden;
        }

        /* ── Fondo ────────────────────────────────── */
        .lr-bg {
          position: fixed;
          inset: 0;
          z-index: 0;
        }
        .lr-bg-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
        }
        .lr-bg-vignette {
          position: absolute;
          inset: 0;
          background: radial-gradient(
            ellipse at center,
            transparent 20%,
            rgba(0,0,0,0.65) 100%
          );
        }
        .lr-bg-overlay {
          position: absolute;
          inset: 0;
          background: radial-gradient(
            ellipse 75% 85% at 50% 50%,
            rgba(3,3,16,0.70) 0%,
            rgba(3,3,16,0.35) 65%,
            transparent 100%
          );
        }

        /* ── Wrap principal ───────────────────────── */
        .lr-wrap {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 400px;
          padding: clamp(36px, 7vh, 64px) 22px clamp(28px, 5.5vh, 52px);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: clamp(18px, 3vh, 28px);
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.65s ease, transform 0.65s ease;
        }
        .lr-wrap--in {
          opacity: 1;
          transform: translateY(0);
        }

        /* ── 1 · Brand logo ───────────────────────── */
        .lr-brand { text-align: center; }
        .lr-brand-img {
          width: clamp(130px, 38vw, 168px);
          height: auto;
          filter: drop-shadow(0 0 18px rgba(168,85,247,0.55));
          animation: brandGlow 4s ease-in-out infinite;
        }
        @keyframes brandGlow {
          0%,100% { filter: drop-shadow(0 0 18px rgba(168,85,247,0.5)); }
          50%      { filter: drop-shadow(0 0 32px rgba(139,92,246,0.85)); }
        }

        /* ── 2 · Card formulario ──────────────────── */
        .lr-card {
          position: relative;
          width: 100%;
          background: rgba(6,6,22,0.68);
          backdrop-filter: blur(22px) saturate(130%);
          border-radius: 18px;
          padding: clamp(22px, 3.5vh, 30px) clamp(20px, 5vw, 26px);
          box-shadow:
            0 2px 40px rgba(0,0,0,0.65),
            0 0 80px rgba(124,58,237,0.06);
        }
        /* Gradient border via pseudo */
        .lr-card-border {
          position: absolute;
          inset: 0;
          border-radius: 18px;
          padding: 1px;
          background: linear-gradient(
            140deg,
            rgba(239,68,68,0.4) 0%,
            rgba(168,85,247,0.3) 45%,
            rgba(56,189,248,0.35) 100%
          );
          -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
        }

        /* ── Account Picker ───────────────────────── */
        .lr-picker {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          padding: 8px 0 4px;
          text-align: center;
        }
        .lr-picker-ico {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: rgba(168,85,247,0.14);
          border: 1px solid rgba(168,85,247,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(168,85,247,0.9);
          margin-bottom: 2px;
        }
        .lr-picker-title {
          font-size: 13px;
          font-weight: 500;
          color: rgba(255,255,255,0.42);
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .lr-picker-email {
          font-size: 15px;
          font-weight: 600;
          color: rgba(255,255,255,0.92);
          word-break: break-all;
        }
        .lr-picker-role {
          display: inline-block;
          font-size: 11px;
          font-weight: 500;
          color: rgba(168,85,247,0.85);
          background: rgba(168,85,247,0.1);
          border: 1px solid rgba(168,85,247,0.22);
          border-radius: 20px;
          padding: 3px 12px;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }
        .lr-switch-btn {
          background: none;
          border: none;
          color: rgba(255,255,255,0.38);
          font-family: 'Exo 2', sans-serif;
          font-size: 12.5px;
          cursor: pointer;
          padding: 4px 8px;
          transition: color 0.2s;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .lr-switch-btn:hover { color: rgba(255,255,255,0.65); }

        /* ── Tabs ─────────────────────────────────── */
        .lr-tabs {
          display: flex;
          border-bottom: 1px solid rgba(255,255,255,0.09);
          margin-bottom: 18px;
          gap: 0;
        }
        .lr-tab {
          flex: 1;
          background: none;
          border: none;
          padding: 10px 0 11px;
          color: rgba(255,255,255,0.38);
          font-family: 'Exo 2', sans-serif;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          letter-spacing: 0.04em;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          transition: color 0.2s, border-color 0.2s;
        }
        .lr-tab:hover { color: rgba(255,255,255,0.65); }
        .lr-tab--active {
          color: rgba(255,255,255,0.92);
          border-bottom-color: rgba(168,85,247,0.75);
        }

        /* ── Success ──────────────────────────────── */
        .lr-success {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 10px 0 6px;
          text-align: center;
        }
        .lr-success-ico { color: #34d399; }
        .lr-success-title {
          font-size: 16px;
          font-weight: 600;
          color: rgba(255,255,255,0.92);
        }
        .lr-success-msg {
          font-size: 12.5px;
          color: rgba(255,255,255,0.5);
          line-height: 1.55;
          max-width: 300px;
        }

        /* ── Form ─────────────────────────────────── */
        .lr-form {
          display: flex;
          flex-direction: column;
          gap: 13px;
        }
        .lr-field-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .lr-label {
          font-size: 11px;
          font-weight: 500;
          color: rgba(255,255,255,0.42);
          letter-spacing: 0.09em;
          text-transform: uppercase;
        }
        .lr-field {
          position: relative;
          display: flex;
          align-items: center;
        }
        .lr-field-ico {
          position: absolute;
          left: 12px;
          color: rgba(255,255,255,0.28);
          pointer-events: none;
          z-index: 1;
        }
        .lr-input {
          width: 100%;
          background: rgba(255,255,255,0.055);
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 11px;
          padding: 11px 14px 11px 36px;
          color: rgba(255,255,255,0.92);
          font-size: 13.5px;
          font-family: 'Exo 2', sans-serif;
          outline: none;
          transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
        }
        .lr-input--pass { padding-right: 40px; }
        .lr-input::placeholder { color: rgba(255,255,255,0.2); }
        .lr-input:focus {
          border-color: rgba(168,85,247,0.52);
          background: rgba(255,255,255,0.08);
          box-shadow: 0 0 0 3px rgba(168,85,247,0.09);
        }
        .lr-eye {
          position: absolute;
          right: 11px;
          background: none;
          border: none;
          color: rgba(255,255,255,0.28);
          cursor: pointer;
          padding: 3px;
          display: flex;
          align-items: center;
          transition: color 0.2s;
        }
        .lr-eye:hover { color: rgba(255,255,255,0.65); }

        /* Error */
        .lr-error {
          display: flex;
          align-items: flex-start;
          gap: 7px;
          background: rgba(239,68,68,0.08);
          border: 1px solid rgba(239,68,68,0.2);
          border-radius: 9px;
          padding: 9px 12px;
          color: #fca5a5;
          font-size: 12px;
          line-height: 1.45;
        }
        .lr-error-ico { margin-top: 1px; flex-shrink: 0; }

        /* Forgot */
        .lr-forgot { text-align: right; }
        .lr-forgot-btn {
          background: none;
          border: none;
          color: rgba(255,255,255,0.32);
          font-size: 11.5px;
          font-family: 'Exo 2', sans-serif;
          cursor: pointer;
          transition: color 0.2s;
          padding: 0;
        }
        .lr-forgot-btn:hover { color: rgba(255,255,255,0.62); }

        /* Submit */
        .lr-submit {
          position: relative;
          width: 100%;
          padding: 13px 20px;
          background: linear-gradient(135deg, #7c3aed 0%, #2563eb 100%);
          border: none;
          border-radius: 11px;
          color: white;
          font-family: 'Exo 2', sans-serif;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.06em;
          cursor: pointer;
          overflow: hidden;
          transition: transform 0.22s ease, box-shadow 0.22s ease;
          box-shadow: 0 4px 22px rgba(124,58,237,0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 46px;
          margin-top: 2px;
        }
        .lr-submit-shine {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #6d28d9 0%, #1d4ed8 100%);
          opacity: 0;
          transition: opacity 0.22s;
        }
        .lr-submit:hover:not(:disabled) .lr-submit-shine { opacity: 1; }
        .lr-submit:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 7px 28px rgba(124,58,237,0.6);
        }
        .lr-submit:active:not(:disabled) { transform: translateY(0); }
        .lr-submit:disabled { opacity: 0.55; cursor: not-allowed; }
        .lr-submit-text { position: relative; z-index: 1; }

        /* Google button */
        .lr-google {
          width: 100%;
          padding: 11px 20px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 11px;
          color: rgba(255,255,255,0.88);
          font-family: 'Exo 2', sans-serif;
          font-size: 13.5px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: background 0.2s, border-color 0.2s;
          min-height: 44px;
        }
        .lr-google:hover:not(:disabled) {
          background: rgba(255,255,255,0.10);
          border-color: rgba(255,255,255,0.22);
        }
        .lr-google:disabled { opacity: 0.55; cursor: not-allowed; }

        /* Divider */
        .lr-divider {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .lr-div-hr {
          flex: 1;
          height: 1px;
          background: rgba(255,255,255,0.09);
        }
        .lr-div-label {
          font-size: 11px;
          color: rgba(255,255,255,0.28);
          letter-spacing: 0.06em;
        }

        /* Spinner */
        .lr-spinner {
          width: 18px; height: 18px;
          border: 2px solid rgba(255,255,255,0.25);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.65s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* ── 3 · Logos de plataforma ──────────────── */
        .lr-platforms {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
        }

        /* Badge común */
        .lr-plat {
          position: relative;
          width: clamp(96px, 24vw, 112px);
          height: clamp(96px, 24vw, 112px);
          border-radius: 16px;
          overflow: hidden;
          background: rgba(0,0,0,0.5);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.08);
          box-shadow: 0 4px 20px rgba(0,0,0,0.5);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          padding: 6px;
        }
        .lr-plat:hover {
          transform: translateY(-4px) scale(1.03);
        }

        /* 2ONA MEDIA — glow rojo */
        .lr-plat--red {
          border-color: rgba(239,68,68,0.22);
          box-shadow:
            0 4px 20px rgba(0,0,0,0.5),
            0 0 22px rgba(239,68,68,0.14);
        }
        .lr-plat--red:hover {
          box-shadow:
            0 8px 30px rgba(0,0,0,0.55),
            0 0 32px rgba(239,68,68,0.28);
        }

        /* Xuper Play — glow azul */
        .lr-plat--blue {
          border-color: rgba(99,179,237,0.22);
          box-shadow:
            0 4px 20px rgba(0,0,0,0.5),
            0 0 22px rgba(99,179,237,0.14);
          background: rgba(8,14,38,0.65);
        }
        .lr-plat--blue:hover {
          box-shadow:
            0 8px 30px rgba(0,0,0,0.55),
            0 0 32px rgba(99,179,237,0.28);
        }

        .lr-plat-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
          border-radius: 10px;
        }

        /* Shine sweep en hover */
        .lr-plat-shine {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            110deg,
            transparent 38%,
            rgba(255,255,255,0.09) 50%,
            transparent 62%
          );
          transform: translateX(-120%);
          transition: transform 0.5s ease;
          pointer-events: none;
        }
        .lr-plat:hover .lr-plat-shine { transform: translateX(120%); }

        /* Separador central */
        .lr-plat-divider {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
          flex-shrink: 0;
        }
        .lr-div-line {
          display: block;
          width: 1px;
          height: 28px;
          background: linear-gradient(to bottom, transparent, rgba(255,255,255,0.18), transparent);
        }
        .lr-div-gem {
          display: block;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: rgba(255,255,255,0.4);
          box-shadow: 0 0 7px rgba(255,255,255,0.5);
        }

        /* ── 4 · Tagline ──────────────────────────── */
        .lr-tagline {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 500;
          color: rgba(255,255,255,0.8);
          letter-spacing: 0.07em;
          text-shadow: 0 1px 10px rgba(0,0,0,0.85), 0 0 24px rgba(0,0,0,0.5);
        }
        .lr-tag-dot {
          display: inline-block;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .lr-tag-dot--red  { background: #ef4444; box-shadow: 0 0 7px #ef4444; }
        .lr-tag-dot--blue { background: #38bdf8; box-shadow: 0 0 7px #38bdf8; }

        /* ── Responsive ───────────────────────────── */
        @media (max-width: 430px) {
          .lr-wrap { max-width: 100%; padding-left: 18px; padding-right: 18px; }
          .lr-plat { width: 92px; height: 92px; border-radius: 14px; }
        }
        @media (max-width: 360px) {
          .lr-plat { width: 82px; height: 82px; }
          .lr-platforms { gap: 12px; }
        }
        @media (max-height: 680px) {
          .lr-wrap { gap: 14px; padding-top: 24px; padding-bottom: 20px; }
          .lr-plat { width: 82px; height: 82px; }
          .lr-brand-img { width: 120px; }
        }
      `}</style>
    </div>
  );
}
